import type { ConversationTurn, ConversationTurnEntry } from '@/index'
import type { ConversationControlsState } from '../types'
import { BLOCKING_CONVERSATION_EVENT_TYPES, EventType } from '@/features/events/shared'
import { isInterrupt } from '@/features/events/conversation/state/types'
import { isCommandPending } from './commandPendingUtils'

export function deriveConversationControlsState(
  conversationTurn: ConversationTurn | undefined,
): ConversationControlsState {
  const noControls: ConversationControlsState = {
    isProcessing: false,
    isPaused: false,
    canPause: false,
    canResume: false,
    canInject: false,
    canReply: false,
  }

  if (!conversationTurn) {
    return noControls
  }

  const latestEvent = conversationTurn.events.at(-1)
  const latestCommand = conversationTurn.userCommands.at(-1)

  const latestInterrupt = [...conversationTurn.userCommands]
    .reverse()
    .find((cmd) => isInterrupt(cmd))

  let isPaused = false
  if (latestInterrupt) {
    const hasCommandAfterInterrupt = conversationTurn.userCommands.some(
      (cmd) => cmd.timestamp > latestInterrupt.timestamp,
    )
    isPaused = !hasCommandAfterInterrupt
  }

  if (isCommandPending(latestCommand, latestEvent)) {
    return {
      isProcessing: !isPaused,
      isPaused,
      canPause: !isPaused,
      canResume: isPaused,
      canInject: true,
      canReply: false,
    }
  }

  if (!latestEvent) {
    return noControls
  }

  if (latestEvent.eventType === EventType.USER_PROMPT) {
    return {
      isProcessing: true,
      isPaused,
      canPause: !isPaused,
      canResume: isPaused,
      canInject: true,
      canReply: false,
    }
  }

  if (isAgentWorkingEvent(latestEvent)) {
    return {
      isProcessing: true,
      isPaused,
      canPause: !isPaused,
      canResume: isPaused,
      canInject: true,
      canReply: false,
    }
  }

  if (isAwaitingUserOptionsEvent(latestEvent)) {
    return noControls
  }

  if (isAwaitingUserReplyEvent(latestEvent)) {
    return {
      isProcessing: false,
      isPaused: false,
      canPause: false,
      canResume: false,
      canInject: false,
      canReply: true,
    }
  }

  return noControls
}

function isAgentWorkingEvent(event: ConversationTurnEntry): boolean {
  return (
    event.eventType === EventType.AGENT_NARRATIVE || event.eventType === EventType.FILE_MODIFICATION
  )
}

function isAwaitingUserOptionsEvent(event: ConversationTurnEntry): boolean {
  return (
    event.eventType !== EventType.AGENT_REPLY &&
    BLOCKING_CONVERSATION_EVENT_TYPES.has(event.eventType)
  )
}

function isAwaitingUserReplyEvent(event: ConversationTurnEntry): boolean {
  return event.eventType === EventType.AGENT_REPLY
}
